
import React, { useState, useEffect } from 'react';
import { Page, Section, SectionType, RotationMode, ProductSourceType } from '../types';
import { useApp } from '../context/AppContext';
import { Sparkles, Edit3, Image as ImageIcon, Settings, Trash2, Plus, Play, Pause, Calendar, Clock, ExternalLink, FileText, X, Download, File, FileCode, FileSpreadsheet, MapPin, Upload, ShoppingCart, Tag, ChevronRight, Folder, SlidersHorizontal, CheckSquare, Square, Check, List, Database, Shuffle, Filter } from 'lucide-react';
import { generateAIContent } from '../services/geminiService';
import * as Icons from 'lucide-react';
import ImagePickerModal from './ImagePickerModal';
import FilePickerModal from './FilePickerModal';
import { AiOnboardingQuiz } from './AiOnboardingQuiz';
import * as ReactRouterDOM from 'react-router-dom';

const { Link, useNavigate } = ReactRouterDOM as any;

interface DynamicPageProps {
  page: Page;
}

const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1518837695005-2083093ee35b?q=80&w=2670&auto=format&fit=crop";

const IconComponent = ({ name, className }: { name: string; className?: string }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const LucideIcon = (Icons as any)[name];
  return LucideIcon ? <LucideIcon className={className} strokeWidth={1.5} /> : <Icons.HelpCircle className={className} strokeWidth={1.5} />;
};

const ActionButton = ({ href, children, className }: { href?: string, children?: React.ReactNode, className?: string }) => {
  if (!href) return null;
  const isInternal = href.startsWith('/');
  if (isInternal) {
    return <Link to={href} className={className}>{children}</Link>;
  }
  return <a href={href} className={className} target="_blank" rel="noopener noreferrer">{children}</a>;
};

// --- Helper for Active Links (Phone, Email, URL) ---
const renderTextWithLinks = (text?: string) => {
  if (!text) return null;

  // Split by URLs first
  const parts1 = text.split(/(https?:\/\/[^\s]+)/g);
  
  return parts1.map((part1, i) => {
    if (part1.match(/^https?:\/\//)) {
      return <a key={`url-${i}`} href={part1} target="_blank" rel="noopener noreferrer" className="text-gray-900 border-b border-gray-300 hover:border-black transition-colors" onClick={e => e.stopPropagation()}>{part1}</a>;
    }

    // Split by Emails
    const parts2 = part1.split(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g);
    
    return parts2.map((part2, j) => {
      if (part2.match(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+$/)) {
        return <a key={`email-${i}-${j}`} href={`mailto:${part2}`} className="text-gray-900 border-b border-gray-300 hover:border-black transition-colors" onClick={e => e.stopPropagation()}>{part2}</a>;
      }

      // Split by Phone Numbers
      const parts3 = part2.split(/(\+?\d{2,3}[ -]?\d{3}[ -]?\d{3}[ -]?\d{3}|\d{3}[ -]?\d{3}[ -]?\d{3})/g);

      return parts3.map((part3, k) => {
         const digits = part3.replace(/\D/g, '');
         if (digits.length >= 9 && (part3.includes('-') || part3.includes(' ') || part3.startsWith('+'))) {
            return <a key={`phone-${i}-${j}-${k}`} href={`tel:${digits}`} className="text-gray-900 border-b border-gray-300 hover:border-black transition-colors" onClick={e => e.stopPropagation()}>{part3}</a>
         }
         return part3;
      });
    });
  });
};

// --- Extracted EditableText Component to prevent focus loss ---
interface EditableTextProps {
    text?: string;
    field: keyof Section;
    className?: string;
    as?: React.ElementType;
    isEditMode: boolean;
    onChange: (value: string) => void;
    onAiClick: () => void;
}

const EditableText: React.FC<EditableTextProps> = ({ 
    text, field, className, as: Tag = 'div', isEditMode, onChange, onAiClick 
}) => {
    if (!isEditMode) {
        return <Tag className={className}>{renderTextWithLinks(text)}</Tag>;
    }

    const editClassName = `${className} !text-gray-900 !bg-white/95 !shadow-lg border border-blue-200 rounded px-2 py-1`;

    return (
      <div className="relative group inline-block w-full z-20">
        <textarea
          className={`w-full transition-all resize-none outline-none focus:ring-2 focus:ring-blue-500/20 ${editClassName}`}
          value={text || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={field === 'content' ? 6 : 1}
          style={{ minHeight: field === 'content' ? '150px' : 'auto' }}
        />
        <button 
          onClick={onAiClick}
          className="absolute -right-3 -top-3 p-2 bg-black text-white rounded-full opacity-100 shadow-xl z-50 hover:scale-110 transition-transform cursor-pointer"
          title="Ulepsz z AI"
        >
          <Sparkles size={14} />
        </button>
      </div>
    );
};

// ... (SourceConfigModal and FilterConfigModal logic remains identical) ...
const SourceConfigModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    section: Section;
    pageId: string;
}> = ({ isOpen, onClose, section, pageId }) => {
    const { updateSection } = useApp();
    const [sourceType, setSourceType] = useState<ProductSourceType>(section.productSource || 'manual');
    const [criteria, setCriteria] = useState(section.sourceCriteria || '');
    const [limit, setLimit] = useState(section.itemLimit || 4);

    useEffect(() => {
        if(isOpen) {
            setSourceType(section.productSource || 'manual');
            setCriteria(section.sourceCriteria || '');
            setLimit(section.itemLimit || 4);
        }
    }, [isOpen, section]);

    const handleSave = () => {
        updateSection(pageId, section.id, {
            productSource: sourceType,
            sourceCriteria: criteria,
            itemLimit: limit
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                        <Database size={20} /> Źródło Produktów
                    </h3>
                    <button onClick={onClose}><X size={20}/></button>
                </div>
                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-3 gap-2 p-1 bg-gray-100 rounded-lg">
                        <button 
                            onClick={() => setSourceType('manual')}
                            className={`py-2 text-xs font-bold rounded-md flex items-center justify-center gap-2 ${sourceType === 'manual' ? 'bg-white shadow text-black' : 'text-gray-500 hover:bg-gray-200'}`}
                        >
                           <List size={14}/> Ręczne
                        </button>
                        <button 
                            onClick={() => setSourceType('dynamic_tag')}
                            className={`py-2 text-xs font-bold rounded-md flex items-center justify-center gap-2 ${sourceType === 'dynamic_tag' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:bg-gray-200'}`}
                        >
                           <Tag size={14}/> Po Etykiecie
                        </button>
                        <button 
                            onClick={() => setSourceType('dynamic_random')}
                            className={`py-2 text-xs font-bold rounded-md flex items-center justify-center gap-2 ${sourceType === 'dynamic_random' ? 'bg-white shadow text-purple-600' : 'text-gray-500 hover:bg-gray-200'}`}
                        >
                           <Shuffle size={14}/> Losowe (Dzień)
                        </button>
                    </div>

                    {sourceType === 'manual' && (
                        <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded border">
                            W trybie ręcznym sam dodajesz i edytujesz produkty w tej sekcji. Produkty są niezależne od innych podstron.
                        </p>
                    )}

                    {sourceType === 'dynamic_tag' && (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-500">
                                Sekcja automatycznie pobierze produkty z całego sklepu, które mają pasującą etykietę (Badge).
                            </p>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Szukana Etykieta (Badge)</label>
                                <input 
                                    value={criteria}
                                    onChange={(e) => setCriteria(e.target.value)}
                                    placeholder="np. Bestseller, Promocja, Nowość"
                                    className="w-full border rounded px-3 py-2 text-sm"
                                />
                            </div>
                        </div>
                    )}

                    {sourceType === 'dynamic_random' && (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-500">
                                Sekcja wyświetli losowe produkty z całego sklepu. 
                                Zestaw zmienia się automatycznie co 24h (rotacja dzienna).
                            </p>
                        </div>
                    )}

                    {sourceType !== 'manual' && (
                        <div>
                             <label className="block text-xs font-bold text-gray-700 mb-1">Limit Produktów</label>
                             <input 
                                type="number"
                                min="1"
                                max="12"
                                value={limit}
                                onChange={(e) => setLimit(parseInt(e.target.value))}
                                className="w-full border rounded px-3 py-2 text-sm"
                             />
                        </div>
                    )}
                </div>
                <div className="p-4 border-t flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded text-sm font-medium">Anuluj</button>
                    <button onClick={handleSave} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-bold">Zapisz Ustawienia</button>
                </div>
            </div>
        </div>
    );
};

const FilterConfigModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    section: Section;
    pageId: string;
}> = ({ isOpen, onClose, section, pageId }) => {
    const { updateSection, config } = useApp();
    const [newFilterLabel, setNewFilterLabel] = useState('');
    const [newFilterOptions, setNewFilterOptions] = useState('');

    const localFilters = section.filterCategories || [];
    const globalFilters = config.globalAttributes?.filter(a => a.categoryIds?.includes(section.id)) || [];
    
    // Merge them, preferring local over global if keys collide
    const availableFiltersMap = new Map();
    [...globalFilters, ...localFilters].forEach(f => {
        availableFiltersMap.set(f.key, f);
    });
    const allFilters = Array.from(availableFiltersMap.values());

    const addFilter = () => {
        if (!newFilterLabel.trim()) return;
        const key = newFilterLabel.toLowerCase()
            .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e').replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o').replace(/ś/g, 's').replace(/ź/g, 'z').replace(/ż/g, 'z')
            .replace(/[^a-z0-9]+/g, '_');
            
        // Trim, filter empty, and deduplicate
        const rawOptions = newFilterOptions
            .split(',')
            .map(o => o.trim())
            .filter(o => o.length > 0);
        const options = Array.from(new Set(rawOptions));

        const current = section.filterCategories || [];
        updateSection(pageId, section.id, {
            filterCategories: [...current, { key, label: newFilterLabel, options }]
        });
        setNewFilterLabel('');
        setNewFilterOptions('');
    };

    const removeFilter = (key: string) => {
        const current = section.filterCategories || [];
        updateSection(pageId, section.id, {
            filterCategories: current.filter(f => f.key !== key)
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center shrink-0">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                        <SlidersHorizontal size={20} /> Konfiguracja Cech i Filtrów lokalnych
                    </h3>
                    <button onClick={onClose}><X size={20}/></button>
                </div>
                <div className="p-6 space-y-6 overflow-y-auto">
                    <p className="text-xs text-gray-500">
                        Zdefiniuj cechy (atrybuty) dla produktów WYŁĄCZNIE dla tej kategorii.
                        Cechy współdzielone (np. Kolor, Rozmiar obuwniczy) możesz przypisać w menu głównym "Globalne Cechy Produktów".
                    </p>
                    
                    <div className="bg-blue-50 p-4 rounded border border-blue-100 space-y-3">
                        <h4 className="text-xs font-bold text-blue-800 uppercase">Dodaj nową cechę lokalną</h4>
                        <div className="space-y-2">
                            <input 
                                className="w-full border rounded px-3 py-2 text-sm"
                                placeholder="Nazwa cechy (np. Kolor, Moc)"
                                value={newFilterLabel}
                                onChange={(e) => setNewFilterLabel(e.target.value)}
                            />
                            <input 
                                className="w-full border rounded px-3 py-2 text-sm"
                                placeholder="Dostępne opcje po przecinku (np. Czarny, Biały, Złoty)"
                                value={newFilterOptions}
                                onChange={(e) => setNewFilterOptions(e.target.value)}
                            />
                            <button onClick={addFilter} className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-bold flex items-center justify-center gap-2 transition-colors">
                                <Plus size={14} /> Dodaj Cechę Lokalną
                            </button>
                        </div>
                    </div>

                    <div>
                        <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Aktywne Cechy w tej kategorii</h4>
                        <div className="bg-gray-50 rounded border divide-y max-h-60 overflow-y-auto">
                            {allFilters.length === 0 && <p className="p-4 text-center text-xs text-gray-400">Brak zdefiniowanych filtrów. Dodaj lokalne lub przypisz globalne.</p>}
                            {allFilters.map(f => {
                                const isGlobal = globalFilters.some(g => g.key === f.key);
                                return (
                                <div key={f.key} className={`p-3 flex justify-between items-center group ${isGlobal ? 'bg-orange-50' : 'bg-white'}`}>
                                    <div>
                                        <span className="font-bold text-sm text-gray-800 flex items-center gap-2">
                                            {f.label} 
                                            {isGlobal && <span className="text-[9px] bg-orange-200 text-orange-800 px-1 py-0.5 rounded uppercase">Globalna</span>}
                                        </span>
                                        {f.options && f.options.length > 0 && (
                                            <span className="text-[10px] text-gray-500 flex items-center gap-1 mt-1">
                                                <List size={10} /> {f.options.join(', ')}
                                            </span>
                                        )}
                                    </div>
                                    {!isGlobal && (
                                        <button onClick={() => removeFilter(f.key)} className="text-gray-400 hover:text-red-600 p-2 hover:bg-red-50 rounded" title="Usuń cechę lokalną">
                                            <Trash2 size={14}/>
                                        </button>
                                    )}
                                </div>
                            )})}
                        </div>
                    </div>
                </div>
                <div className="p-4 border-t flex justify-end shrink-0">
                    <button onClick={onClose} className="px-6 py-2 bg-gray-900 hover:bg-black text-white rounded text-sm font-bold transition-colors">Zamknij</button>
                </div>
            </div>
        </div>
    );
};

const CategorySidebar: React.FC<{ sections: Section[] }> = ({ sections }) => {
    const [activeSectionId, setActiveSectionId] = useState<string>('');

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSectionId(entry.target.id);
                    }
                });
            },
            {
                rootMargin: '-20% 0px -60% 0px', // Trigger when section is near the top
                threshold: 0
            }
        );

        sections.forEach((section) => {
            const el = document.getElementById(section.id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [sections]);

    return (
        <div className="hidden lg:block w-64 flex-shrink-0 sticky top-32 self-start animate-fade-in pr-8 border-r border-gray-100">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-8 flex items-center gap-2">
                <Folder size={14}/> Kategorie
            </h3>
            <ul className="space-y-4">
                {sections.map(s => {
                    const isActive = activeSectionId === s.id;
                    return (
                        <li key={s.id}>
                            <a 
                                href={`#${s.id}`} 
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className={`group flex items-center justify-between text-sm transition-all duration-300 ${isActive ? 'text-black font-medium translate-x-1' : 'text-gray-500 hover:text-gray-800'}`}
                            >
                                <span>{s.title || 'Sekcja bez tytułu'}</span>
                                {isActive && <ChevronRight size={14} className="text-black animate-pulse" />}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const DynamicPage: React.FC<DynamicPageProps> = ({ page }) => {
    const { config } = useApp();

    useEffect(() => {
        document.title = `${page.title} - ${config.companyName}`;
    }, [page.title, config]);

    // Grouping Logic for "Tree View" layout
    const renderedContent: React.ReactNode[] = [];
    const productGridBuffer: Section[] = [];

    const flushProductBuffer = (keyPrefix: number) => {
        if (productGridBuffer.length === 0) return null;
        
        const hasSidebar = productGridBuffer.length > 1;
        const sectionsToRender = [...productGridBuffer];
        productGridBuffer.length = 0; // clear

        if (!hasSidebar) {
            return sectionsToRender.map(section => (
                <SectionRenderer key={section.id} section={section} pageId={page.id} />
            ));
        }

        return (
            <div key={`group-${keyPrefix}`} className="container mx-auto px-6 py-12 flex flex-col lg:flex-row gap-12 border-t border-gray-100 bg-[#fcfcfc]">
                <CategorySidebar sections={sectionsToRender} />
                <div className="flex-1 min-w-0">
                    {sectionsToRender.map(section => (
                        <div key={section.id} id={section.id} className="scroll-mt-32 pb-16 last:pb-0 mb-16 last:mb-0">
                             <SectionRenderer section={section} pageId={page.id} isGrouped={true} />
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    page.sections.forEach((section, index) => {
        if (section.type === SectionType.PRODUCT_GRID) {
            productGridBuffer.push(section);
        } else {
            const bufferedContent = flushProductBuffer(index);
            if (bufferedContent) {
                if (Array.isArray(bufferedContent)) renderedContent.push(...bufferedContent);
                else renderedContent.push(bufferedContent);
            }
            renderedContent.push(
                <SectionRenderer key={section.id} section={section} pageId={page.id} />
            );
        }
    });
    
    const remainingBuffer = flushProductBuffer((page.sections || []).length);
    if (remainingBuffer) {
        if (Array.isArray(remainingBuffer)) renderedContent.push(...remainingBuffer);
        else renderedContent.push(remainingBuffer);
    }

  return (
    <div className="animate-fade-in">
      {renderedContent}
    </div>
  );
};

const RotationConfigModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    section: Section;
    pageId: string;
    onAddImage: () => void;
}> = ({ isOpen, onClose, section, pageId, onAddImage }) => {
    const { updateSection } = useApp();
    const [newImageUrl, setNewImageUrl] = useState('');

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[160] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                        <Settings size={20} /> Konfiguracja Rotacji
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
                </div>
                <div className="p-6 overflow-y-auto space-y-6">
                   <div className="flex gap-2">
                      <input type="checkbox" checked={section.isAutoRotationEnabled} onChange={(e) => updateSection(pageId, section.id, { isAutoRotationEnabled: e.target.checked })} />
                      <span>Włącz rotację</span>
                   </div>
                   <button onClick={onAddImage} className="text-sm underline">Dodaj zdjęcie</button>
                </div>
                <div className="p-4 bg-gray-50 border-t flex justify-end">
                    <button onClick={onClose} className="px-6 py-2 bg-black text-white rounded-sm text-sm hover:bg-gray-800">
                        Zamknij
                    </button>
                </div>
            </div>
        </div>
    );
};

const SectionRenderer: React.FC<{ section: Section; pageId: string; isGrouped?: boolean }> = ({ section, pageId, isGrouped }) => {
  const { isEditMode, updateSection, addToCart, config, getAllProducts, toggleFavorite, favorites } = useApp();
  const [aiPrompt, setAiPrompt] = React.useState('');
  const [isAiModalOpen, setIsAiModalOpen] = React.useState(false);
  const [targetField, setTargetField] = React.useState<keyof Section | null>(null);
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);
  const [isRotationConfigOpen, setIsRotationConfigOpen] = React.useState(false);
  const [isFilterConfigOpen, setIsFilterConfigOpen] = React.useState(false);
  const [isSourceConfigOpen, setIsSourceConfigOpen] = React.useState(false);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [editingFileIndex, setEditingFileIndex] = useState<number | null>(null);
  const [editingItemIndex, setEditingItemIndex] = React.useState<number | null>(null);
  const [editingGalleryIndex, setEditingGalleryIndex] = React.useState<number | null>(null);

  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
  const [sortOption, setSortOption] = useState<string>('default');

  useEffect(() => {
    if (!section.isAutoRotationEnabled || !section.galleryImages || section.galleryImages.length <= 1) {
        setCurrentImageIndex(0);
        return;
    }
    const imagesCount = section.galleryImages.length;
    let intervalId: ReturnType<typeof setInterval>;
    if (section.rotationMode === 'timer') {
        const intervalMs = (section.rotationInterval || 5) * 1000;
        intervalId = setInterval(() => {
            setCurrentImageIndex(prev => (prev + 1) % imagesCount);
        }, intervalMs);
    } 
    return () => {
        if (intervalId) clearInterval(intervalId);
    };
  }, [section.isAutoRotationEnabled, section.rotationMode, section.rotationInterval, section.galleryImages]);

  // ... (handleTextEdit, openAiModal, openImageModal, handleImageSave, handleFileSave, handleAiGenerate, handleAddProduct logic remains the same) ...

  const handleTextEdit = (field: keyof Section, value: string) => {
    updateSection(pageId, section.id, { [field]: value });
  };

  const openAiModal = (field: keyof Section) => {
    setTargetField(field);
    setIsAiModalOpen(true);
  };

  const openImageModal = () => {
    setEditingItemIndex(null); 
    setEditingGalleryIndex(null);
    setIsImageModalOpen(true);
  };

  const handleImageSave = (urlOrUrls: string | string[]) => {
    const urls = Array.isArray(urlOrUrls) ? urlOrUrls : [urlOrUrls];
    const firstUrl = urls[0];

    if (editingItemIndex !== null) {
        const newItems = [...(section.items || [])];
        const item = newItems[editingItemIndex];
        
        const currentGallery = item.gallery && item.gallery.length > 0 
            ? [...item.gallery] 
            : [item.imageUrl || FALLBACK_IMAGE];

        if (editingGalleryIndex !== null) {
             if (editingGalleryIndex === -1) {
                 currentGallery.push(...urls);
             } else {
                 currentGallery[editingGalleryIndex] = firstUrl;
             }
             newItems[editingItemIndex] = { 
                 ...item, 
                 gallery: currentGallery,
                 imageUrl: currentGallery[0] 
             };
        } else {
            const otherImages = currentGallery.filter(img => img !== firstUrl);
            const newGallery = [firstUrl, ...otherImages];

            newItems[editingItemIndex] = { 
                ...item, 
                imageUrl: firstUrl,
                gallery: newGallery 
            };
        }
        updateSection(pageId, section.id, { items: newItems });

    } else if (editingGalleryIndex !== null) {
        const currentGallery = section.galleryImages && section.galleryImages.length > 0 ? [...section.galleryImages] : [section.imageUrl || FALLBACK_IMAGE];
        if (editingGalleryIndex === -1) {
            const newGallery = [...currentGallery, ...urls];
            updateSection(pageId, section.id, { 
                galleryImages: newGallery,
                imageUrl: newGallery[0] 
            });
        } else {
            currentGallery[editingGalleryIndex] = firstUrl;
            updateSection(pageId, section.id, {
                galleryImages: currentGallery,
                imageUrl: currentGallery[0] 
            });
        }
    } else {
        const currentGallery: string[] = section.galleryImages || [];
        const newGallery = currentGallery.includes(firstUrl) ? currentGallery : [...currentGallery, ...urls];
        updateSection(pageId, section.id, { 
            imageUrl: newGallery[0],
            galleryImages: newGallery
        });
    }
    setIsImageModalOpen(false);
    setEditingItemIndex(null);
    setEditingGalleryIndex(null);
  };

  const handleFileSave = (url: string, meta: { name: string; size: string; type: string }) => {
    if (editingFileIndex !== null && section.items) {
      const newItems = [...section.items];
      const item = newItems[editingFileIndex];
      newItems[editingFileIndex] = {
        ...item,
        link: url,
        description: meta.size ? `${meta.size} • ${meta.type}` : item.description
      };
      updateSection(pageId, section.id, { items: newItems });
    }
    setIsFileModalOpen(false);
    setEditingFileIndex(null);
  };
  
  const handleAiGenerate = async () => {
    if (!targetField) return;
    setLoading(true);
    const currentContent = String(section[targetField] || '');
    const newContent = await generateAIContent(aiPrompt, currentContent, targetField === 'title' ? 'title' : 'content');
    handleTextEdit(targetField, newContent);
    setLoading(false);
    setIsAiModalOpen(false);
    setAiPrompt('');
  };

  const handleAddProduct = () => {
    const newItems = [...(section.items || [])];
    newItems.push({
      id: `new-${Date.now()}`,
      title: 'Nowy Produkt',
      description: 'Opis...',
      price: '000',
      imageUrl: FALLBACK_IMAGE
    });
    updateSection(pageId, section.id, { items: newItems });
  };

  // --- DYNAMIC PRODUCT SOURCE LOGIC ---
  const getDisplayItems = (): any[] => {
    const limit = section.itemLimit || 4;

    if (section.productSource === 'dynamic_tag') {
        const criteria = (section.sourceCriteria || '').toLowerCase();
        if (!criteria) return [];
        const all = getAllProducts();
        return all.filter(p => p.badge && p.badge.toLowerCase().includes(criteria)).slice(0, limit);
    } 
    else if (section.productSource === 'dynamic_random') {
        const all = getAllProducts();
        const today = new Date().toDateString();
        let seed = 0;
        for(let i=0; i<today.length; i++) seed += today.charCodeAt(i);
        
        const shuffled = [...all].sort((a, b) => {
            const valA = (a.id.charCodeAt(0) + seed) % 100;
            const valB = (b.id.charCodeAt(0) + seed) % 100;
            return valA - valB;
        });
        return shuffled.slice(0, limit);
    }
    
    // Default Manual
    return section.items || [];
  };

  const displayItems: any[] = getDisplayItems();
  const isDynamic = section.productSource && section.productSource !== 'manual';

  // --- FILTERING LOGIC ---
  const toggleFilter = (key: string, value: string) => {
      setActiveFilters(prev => {
          const currentValues = prev[key] || [];
          if (currentValues.includes(value)) {
              const newValues = currentValues.filter(v => v !== value);
              if (newValues.length === 0) {
                  const copy = { ...prev };
                  delete copy[key];
                  return copy;
              }
              return { ...prev, [key]: newValues };
          } else {
              return { ...prev, [key]: [...currentValues, value] };
          }
      });
  };

  const filteredItems = displayItems.filter((item: any) => {
      // If no filters active, return true
      if (Object.keys(activeFilters).length === 0) return true;

      // Check if item matches ALL active filter categories (AND logic between categories)
      return Object.entries(activeFilters).every(([key, validValues]) => {
           const selectedOptions = validValues as string[];
           
           // 1. Check Main Product Attributes
           const itemAttributeString = item.attributes?.[key];
           let productOptions: string[] = [];
           if (itemAttributeString) {
               productOptions = itemAttributeString.split(',').map((s: string) => s.trim());
           }

           // 2. Check Variants Attributes (DEEP CHECK)
           if (item.variants && item.variants.length > 0) {
               item.variants.forEach((v: any) => {
                   const vAttr = v.attributes?.[key];
                   if (vAttr) {
                       productOptions.push(...vAttr.split(',').map((s: string) => s.trim()));
                   }
                   // Also allow matching against variant name if simple
                   if (v.name) productOptions.push(v.name);
               });
           }

           // Check if ANY of the product's options (main or variants) match ANY of the selected filter values
           return productOptions.some(opt => selectedOptions.includes(opt));
      });
  });

  // Merge local and global filters for the store view, and ALSO automatically extract from products
  const sectionFilters = section.filterCategories || [];
  const globalFilters = config.globalAttributes?.filter(a => a.categoryIds?.includes(section.id)) || [];
  const filtersMap = new Map();
  
  // First, auto-harvest all attribute keys from all displayItems
  displayItems.forEach((item: any) => {
      if (item.attributes) {
          Object.keys(item.attributes).forEach(key => {
              if (!filtersMap.has(key)) {
                  filtersMap.set(key, { key, label: key.charAt(0).toUpperCase() + key.slice(1) });
              }
          });
      }
      if (item.variants) {
          item.variants.forEach((v: any) => {
               if (v.attributes) {
                    Object.keys(v.attributes).forEach(key => {
                        if (!filtersMap.has(key)) {
                            filtersMap.set(key, { key, label: key.charAt(0).toUpperCase() + key.slice(1) });
                        }
                    });
               }
          });
      }
  });

  // Override with explicit configs if they exist
  [...globalFilters, ...sectionFilters].forEach(f => filtersMap.set(f.key, f));
  const activeFiltersForCategory = Array.from(filtersMap.values());

  const availableFilters = activeFiltersForCategory.map(cat => {
      // Find all unique values for this key in current items
      const values = new Set<string>();
      displayItems.forEach((item: any) => {
          // Check main product attributes
          if (item.attributes?.[cat.key]) {
              const opts = item.attributes[cat.key].split(',').map((s: string) => s.trim());
              opts.forEach(opt => { if(opt) values.add(opt); });
          }
          // Check variant attributes
          if (item.variants) {
              item.variants.forEach((v: any) => {
                  if (v.attributes?.[cat.key]) {
                      const vOpts = v.attributes[cat.key].split(',').map((s: string) => s.trim());
                      vOpts.forEach(opt => { if(opt) values.add(opt); });
                  }
              });
          }
      });
      return { ...cat, values: Array.from(values).sort() };
  }).filter(cat => cat.values.length > 0); // Only show filters that have values

  const sortedAndFilteredItems = [...filteredItems].sort((a, b) => {
      if (sortOption === 'price_asc') {
          return parseFloat(a.price || '0') - parseFloat(b.price || '0');
      }
      if (sortOption === 'price_desc') {
          return parseFloat(b.price || '0') - parseFloat(a.price || '0');
      }
      if (sortOption === 'name_asc') {
          return a.title.localeCompare(b.title);
      }
      if (sortOption === 'name_desc') {
          return b.title.localeCompare(a.title);
      }
      return 0; // default
  });

  // --- RENDERERS ---

  let content = null;

  switch (section.type) {
    case SectionType.HERO:
        const gallery = section.galleryImages && section.galleryImages.length > 0 
            ? section.galleryImages 
            : [section.imageUrl || FALLBACK_IMAGE];
        const safeIndex = currentImageIndex >= gallery.length ? 0 : currentImageIndex;
      content = (
        <section className="relative h-[70vh] min-h-[600px] flex items-center justify-center text-white overflow-hidden group">
          {gallery.map((img, idx) => (
             <div 
               key={idx}
               className={`absolute inset-0 z-0 transition-opacity duration-1500 ease-in-out ${idx === safeIndex ? 'opacity-100' : 'opacity-0'} ${!section.isAutoRotationEnabled && idx !== safeIndex ? 'hidden' : ''}`}
             >
                <img 
                    src={img} 
                    alt="Background"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
                />
             </div>
          ))}
          <div className="absolute inset-0 bg-black/30 z-10" />
          
          <div className="relative z-20 text-center container px-4 max-w-4xl">
            <div className="mb-6 opacity-0 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <EditableText as="h1" text={section.title} field="title" isEditMode={isEditMode} onChange={(v) => handleTextEdit('title', v)} onAiClick={() => openAiModal('title')} className="text-5xl md:text-7xl font-serif tracking-tight leading-none text-white drop-shadow-sm" />
            </div>
            <div className="mb-10 opacity-0 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                <EditableText as="p" text={section.subtitle} field="subtitle" isEditMode={isEditMode} onChange={(v) => handleTextEdit('subtitle', v)} onAiClick={() => openAiModal('subtitle')} className="text-lg md:text-xl font-light tracking-wide text-white/90 max-w-2xl mx-auto" />
            </div>
            <div className="opacity-0 animate-fade-in flex flex-col sm:flex-row items-center justify-center gap-4" style={{ animationDelay: '0.6s' }}>
                {section.buttonText && (
                  <ActionButton href={section.buttonLink} className="bg-white text-black px-10 py-4 uppercase tracking-[0.2em] text-xs font-bold hover:bg-black hover:text-white transition-colors duration-300 inline-block border border-white">
                    {section.buttonText}
                  </ActionButton>
                )}
                <AiOnboardingQuiz />
            </div>
          </div>
          
          {isEditMode && (
             <div className="absolute top-8 right-8 z-50 flex gap-2">
               <button onClick={openImageModal} className="bg-white/90 text-black px-3 py-1 text-xs rounded shadow"><ImageIcon size={14}/></button>
               <button onClick={() => setIsRotationConfigOpen(true)} className="bg-white/90 text-black px-3 py-1 text-xs rounded shadow"><Settings size={14}/></button>
             </div>
          )}
        </section>
      );
      break;

    case SectionType.PRODUCT_GRID:
        content = (
          <section className={`${isGrouped ? 'py-4' : 'py-24 bg-[#fcfcfc]'}`}>
            <div className={isGrouped ? 'w-full' : 'container mx-auto px-6'}>
               <div className="text-left mb-8 border-b border-gray-100 pb-4 flex justify-between items-end">
                 <div>
                    <EditableText as="h2" text={section.title} field="title" isEditMode={isEditMode} onChange={(v) => handleTextEdit('title', v)} onAiClick={() => openAiModal('title')} className="text-3xl font-serif text-gray-900 mb-2" />
                    <EditableText as="p" text={section.subtitle} field="subtitle" isEditMode={isEditMode} onChange={(v) => handleTextEdit('subtitle', v)} onAiClick={() => openAiModal('subtitle')} className="text-gray-500 font-light tracking-wide uppercase text-xs" />
                    {isDynamic && (
                        <span className="text-[10px] text-blue-500 font-bold uppercase tracking-widest mt-1 inline-block">
                            Tryb Automatyczny: {section.productSource === 'dynamic_tag' ? `Tag "${section.sourceCriteria}"` : 'Rotacja Losowa'}
                        </span>
                    )}
                 </div>
                 
                 <div className="flex items-center gap-4">
                     {/* Sorting Select */}
                     <select 
                         value={sortOption} 
                         onChange={(e) => setSortOption(e.target.value)}
                         className="text-xs border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:border-black cursor-pointer text-gray-600 shadow-sm"
                     >
                         <option value="default">Domyślne polecane</option>
                         <option value="price_asc">Cena: od najniższej</option>
                         <option value="price_desc">Cena: od najwyższej</option>
                         <option value="name_asc">Nazwa: A do Z</option>
                         <option value="name_desc">Nazwa: Z do A</option>
                     </select>
                     
                     {isEditMode && (
                         <div className="flex gap-2">
                             <button 
                                 onClick={() => setIsSourceConfigOpen(true)}
                                 className="text-xs bg-gray-900 hover:bg-black text-white px-3 py-2 rounded flex items-center gap-2 shadow-sm"
                                 title="Źródło Produktów"
                             >
                                 <Database size={14}/> {isDynamic ? 'Auto' : 'Ręczne'}
                             </button>
                             <button 
                                onClick={() => setIsFilterConfigOpen(true)}
                                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded flex items-center gap-2"
                             >
                                 <SlidersHorizontal size={14}/> Konfiguruj Filtry
                             </button>
                         </div>
                     )}
                 </div>
               </div>

               <div className="flex flex-col md:flex-row gap-8">
                  {/* Sidebar Filters */}
                  {availableFilters.length > 0 && (
                      <div className="w-full md:w-48 flex-shrink-0 space-y-6">
                           <div className="flex justify-between items-center mb-2">
                               <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Filtrowanie</span>
                               {Object.keys(activeFilters).length > 0 && (
                                   <button onClick={() => setActiveFilters({})} className="text-[10px] text-red-500 hover:underline">Reset</button>
                               )}
                           </div>
                           {availableFilters.map(filter => (
                               <div key={filter.key}>
                                   <h4 className="font-bold text-sm mb-2">{filter.label}</h4>
                                   <div className="space-y-1">
                                       {filter.values.map(val => {
                                           const isChecked = activeFilters[filter.key]?.includes(val);
                                           return (
                                               <label key={val} className="flex items-center gap-2 cursor-pointer group">
                                                   <div className={`w-4 h-4 border rounded flex items-center justify-center transition-colors ${isChecked ? 'bg-black border-black text-white' : 'bg-white border-gray-300 group-hover:border-gray-400'}`}>
                                                       {isChecked && <Check size={10} strokeWidth={3}/>}
                                                   </div>
                                                   <input 
                                                      type="checkbox" 
                                                      className="hidden"
                                                      checked={isChecked}
                                                      onChange={() => toggleFilter(filter.key, val)}
                                                   />
                                                   <span className={`text-xs ${isChecked ? 'font-bold text-black' : 'text-gray-600'}`}>{val}</span>
                                               </label>
                                           )
                                       })}
                                   </div>
                               </div>
                           ))}
                      </div>
                  )}

                  <div className={`flex-1 grid grid-cols-1 sm:grid-cols-2 ${isGrouped ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-x-6 gap-y-12`}>
                      {sortedAndFilteredItems.map((item, idx) => {
                          // Find original index for editing (only relevant in Manual mode)
                          const originalIdx = (section.items || []).findIndex(i => i.id === item.id);
                          
                          // --- AUTO-SELECT LOGIC ---
                          // If filtering is active, pass the filter value to product page
                          const queryParams = new URLSearchParams();
                          Object.entries(activeFilters).forEach(([, values]) => {
                             if(values.length > 0) queryParams.set('preselect', values[0]);
                          });
                          const linkSearch = queryParams.toString();
                          const productLink = `/product/${item.id}${linkSearch ? '?' + linkSearch : ''}`;
                          const isFavorite = item.id ? favorites.includes(item.id) : false;

                          return (
                            <div key={item.id || idx} className="group flex flex-col relative animate-fade-in">
                                {/* Image Area - Link to details */}
                                <div className="relative">
                                    <Link to={productLink} className="block aspect-[4/5] bg-[#f5f5f5] mb-4 overflow-hidden relative cursor-pointer">
                                        <img 
                                            src={item.imageUrl || FALLBACK_IMAGE}
                                            alt={item.title}
                                            className="w-full h-full object-cover mix-blend-multiply group-hover:scale-105 transition-transform duration-700 ease-in-out"
                                        />
                                        {item.badge && (
                                            <span className="absolute top-4 left-4 text-[10px] uppercase tracking-widest font-bold bg-white/90 px-2 py-1 text-black">
                                                {isEditMode ? item.badge : item.badge}
                                            </span>
                                        )}
                                        {isEditMode && !isDynamic && (
                                            <button 
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingItemIndex(originalIdx); setIsImageModalOpen(true); }}
                                                className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 flex items-center justify-center text-white z-10"
                                            >
                                                Zmień Zdjęcie
                                            </button>
                                        )}
                                        
                                        {isEditMode && (
                                            <div className="absolute top-2 right-2 z-30">
                                               <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(productLink); }} className="bg-white text-black p-2 rounded-full shadow hover:bg-gray-100"><Edit3 size={14}/></button>
                                            </div>
                                        )}

                                        {/* Quick Add to Cart Overlay */}
                                        <div className="absolute bottom-0 left-0 w-full bg-white/90 py-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300 flex justify-center items-center opacity-0 group-hover:opacity-100 z-20">
                                            <button 
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    addToCart({
                                                        id: `${item.id}`,
                                                        productId: item.id || '',
                                                        title: item.title,
                                                        price: parseFloat(item.price || '0'),
                                                        imageUrl: item.imageUrl || '',
                                                        quantity: 1,
                                                        shippingSize: item.shippingSize || 'parcel_locker'
                                                    })
                                                }}
                                                className="uppercase tracking-widest text-[10px] font-bold text-black hover:text-gray-600 flex items-center gap-2"
                                            >
                                                <Plus size={12}/> Do koszyka
                                            </button>
                                        </div>
                                    </Link>
                                    
                                    {/* Favorite Toggle Button */}
                                    <button 
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (item.id) toggleFavorite(item.id); }}
                                        className={`absolute top-4 right-4 z-20 p-2 rounded-full transition-all duration-300 ${isFavorite ? 'bg-white text-red-500 opacity-100 shadow-md scale-110' : 'bg-white/50 text-gray-500 opacity-0 group-hover:opacity-100 hover:bg-white hover:text-red-400 scale-100'}`}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill={isFavorite ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
                                    </button>
                                </div>

                                {/* Content Area - Typography focused */}
                                <div className="text-left">
                                    {isEditMode && !isDynamic ? (
                                        <div className="space-y-2">
                                            <input className="w-full font-serif text-lg border-b" value={item.title} onChange={(e) => { const n=[...(section.items||[])]; n[originalIdx].title=e.target.value; updateSection(pageId, section.id, {items:n}) }} />
                                            <input className="w-full text-sm border-b" value={item.price} onChange={(e) => { const n=[...(section.items||[])]; n[originalIdx].price=e.target.value; updateSection(pageId, section.id, {items:n}) }} />
                                            <button onClick={() => { const n=[...(section.items||[])]; n.splice(originalIdx, 1); updateSection(pageId, section.id, {items:n}) }} className="text-red-500 text-xs">Usuń</button>
                                        </div>
                                    ) : (
                                        <Link to={productLink} className="block">
                                            <h3 className="font-serif text-base text-gray-900 mb-1 leading-snug group-hover:underline decoration-1 underline-offset-4">{item.title}</h3>
                                            
                                            {/* Rating Stars */}
                                            {item.rating !== undefined && (
                                                <div className="flex items-center gap-1 mb-1">
                                                    <div className="flex text-yellow-400">
                                                        {[1,2,3,4,5].map(star => (
                                                            <svg key={star} xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill={star <= item.rating! ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                                        ))}
                                                    </div>
                                                    <span className="text-[9px] text-gray-400">({item.reviewsCount || 0})</span>
                                                </div>
                                            )}

                                            <p className="text-[10px] text-gray-400 mb-2 uppercase tracking-wide truncate">{item.description}</p>
                                            <div className="flex justify-start gap-3 items-baseline">
                                                {item.oldPrice && <span className="text-xs text-gray-400 line-through">{item.oldPrice} PLN</span>}
                                                <span className="text-xs font-bold text-black">{item.price} PLN</span>
                                            </div>
                                        </Link>
                                    )}
                                </div>
                            </div>
                          );
                      })}
                      
                      {isEditMode && !isDynamic && (
                          <button onClick={handleAddProduct} className="aspect-[4/5] border border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:text-black hover:border-black transition-colors">
                            <Plus size={32}/>
                          </button>
                      )}
                  </div>
               </div>
            </div>
          </section>
        );
        break;

    case SectionType.TEXT_IMAGE:
       const images = section.galleryImages && section.galleryImages.length > 0 ? section.galleryImages : [section.imageUrl || FALLBACK_IMAGE];
       content = (
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1 max-w-lg">
               <span className="block w-12 h-0.5 bg-black mb-6"></span>
               <EditableText as="h2" text={section.title} field="title" isEditMode={isEditMode} onChange={(v) => handleTextEdit('title', v)} onAiClick={() => openAiModal('title')} className="text-4xl font-serif text-gray-900 mb-8 leading-tight" />
               <EditableText as="div" text={section.content} field="content" isEditMode={isEditMode} onChange={(v) => handleTextEdit('content', v)} onAiClick={() => openAiModal('content')} className="text-gray-600 leading-relaxed mb-8 text-sm font-light tracking-wide whitespace-pre-wrap" />
               {section.buttonText && (
                 <ActionButton href={section.buttonLink} className="text-black uppercase tracking-[0.2em] text-[10px] font-bold border-b border-gray-300 pb-1 hover:border-black transition-colors inline-block">
                   {section.buttonText}
                 </ActionButton>
               )}
            </div>
            <div className="order-1 md:order-2">
                 <div className="relative overflow-hidden aspect-[4/5] bg-gray-100">
                     <img src={images[0]} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
                     {isEditMode && <button onClick={() => { setEditingGalleryIndex(0); setIsImageModalOpen(true); }} className="absolute inset-0 bg-black/20 text-white flex items-center justify-center opacity-0 hover:opacity-100">Edytuj</button>}
                 </div>
            </div>
          </div>
        </section>
       );
       break;

    case SectionType.RICH_TEXT:
        content = (
            <section className="py-16 bg-white">
                <div className="container mx-auto px-6 max-w-4xl">
                    <div className="mb-8 pb-4 border-b border-gray-100">
                        <EditableText as="h1" text={section.title} field="title" isEditMode={isEditMode} onChange={(v) => handleTextEdit('title', v)} onAiClick={() => openAiModal('title')} className="text-3xl font-serif text-gray-900" />
                        <EditableText as="p" text={section.subtitle} field="subtitle" isEditMode={isEditMode} onChange={(v) => handleTextEdit('subtitle', v)} onAiClick={() => openAiModal('subtitle')} className="text-gray-500 text-sm mt-2 uppercase tracking-wide" />
                    </div>
                    <EditableText as="div" text={section.content} field="content" isEditMode={isEditMode} onChange={(v) => handleTextEdit('content', v)} onAiClick={() => openAiModal('content')} className="prose prose-sm prose-gray max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap" />
                </div>
            </section>
        );
        break;

    case SectionType.FEATURES:
        content = (
        <section className="py-24 bg-[#f5f5f5]">
           <div className="container mx-auto px-6">
             <div className="text-center mb-20 max-w-2xl mx-auto">
               <EditableText as="h2" text={section.title} field="title" isEditMode={isEditMode} onChange={(v) => handleTextEdit('title', v)} onAiClick={() => openAiModal('title')} className="text-3xl font-serif text-gray-900 mb-4" />
               <EditableText as="p" text={section.subtitle} field="subtitle" isEditMode={isEditMode} onChange={(v) => handleTextEdit('subtitle', v)} onAiClick={() => openAiModal('subtitle')} className="text-xs uppercase tracking-widest text-gray-500" />
             </div>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center divide-y md:divide-y-0 md:divide-x divide-gray-200">
               {section.items?.map((item, idx) => (
                   <div key={idx} className="pt-8 md:pt-0 px-4">
                        <div className="text-gray-800 mb-6 flex justify-center"><IconComponent name={item.icon || 'Check'} className="w-8 h-8 opacity-80" /></div>
                        {isEditMode ? (
                           <input value={item.title} onChange={e => { const n=[...(section.items||[])]; n[idx].title=e.target.value; updateSection(pageId, section.id, {items:n}) }} className="font-serif text-center w-full"/>
                        ) : <h3 className="text-xl font-serif mb-3 text-gray-900">{item.title}</h3>}
                        <p className="text-gray-500 text-xs leading-relaxed max-w-xs mx-auto">{renderTextWithLinks(item.description)}</p>
                   </div>
               ))}
             </div>
           </div>
        </section>
        );
        break;

    case SectionType.CTA:
      content = (
        <section className="py-24 bg-black text-white">
          <div className="container mx-auto px-6 text-center">
             <EditableText as="h2" text={section.title} field="title" isEditMode={isEditMode} onChange={(v) => handleTextEdit('title', v)} onAiClick={() => openAiModal('title')} className="text-4xl font-serif mb-6" />
             <EditableText as="p" text={section.content} field="content" isEditMode={isEditMode} onChange={(v) => handleTextEdit('content', v)} onAiClick={() => openAiModal('content')} className="text-sm font-light tracking-wide opacity-80 max-w-xl mx-auto mb-10" />
             {section.buttonText && (
               <ActionButton href={section.buttonLink} className="inline-block bg-white text-black px-10 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gray-200 transition-colors">
                   {section.buttonText}
               </ActionButton>
             )}
          </div>
        </section>
      );
      break;

    case SectionType.MAP:
        const mapAddress = config.address || "Warszawa";
        const mapUrl = `https://maps.google.com/maps?q=${encodeURIComponent(mapAddress)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
        content = (
            <section className="py-0 h-[400px] relative grayscale">
                <iframe width="100%" height="100%" src={mapUrl} className="absolute inset-0"></iframe>
                <div className="absolute bottom-8 left-8 bg-white p-6 shadow-soft max-w-xs">
                     <h3 className="font-serif text-xl mb-2 flex items-center gap-2 text-gray-900">{section.title || "Lokalizacja"}</h3>
                     <p className="text-xs text-gray-500 uppercase tracking-wide leading-relaxed">{mapAddress}</p>
                </div>
            </section>
        );
        break;
      
    default:
        content = <div className="p-8 text-center text-gray-400 font-serif italic">Sekcja w przygotowaniu</div>;
  }

  return (
    <div className="relative group">
       {content}

       {/* Modals remain the same */}
       {isAiModalOpen && (
           <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
             <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-lg">
               <textarea className="w-full border rounded-lg p-3 h-32 mb-4" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} autoFocus placeholder="Opisz co chcesz wygenerować..." />
               <div className="flex justify-end gap-3"><button onClick={() => setIsAiModalOpen(false)}>Anuluj</button><button onClick={handleAiGenerate} disabled={loading}>{loading ? 'Generowanie...' : 'Generuj'}</button></div>
             </div>
           </div>
       )}

       <ImagePickerModal isOpen={isImageModalOpen} onClose={() => setIsImageModalOpen(false)} onSave={handleImageSave} initialUrl={editingItemIndex !== null ? section.items?.[editingItemIndex].imageUrl : section.imageUrl} allowMultiple={editingGalleryIndex === -1} contextName={editingItemIndex !== null ? section.items?.[editingItemIndex].title : section.title} />
       <FilePickerModal isOpen={isFileModalOpen} onClose={() => setIsFileModalOpen(false)} onSave={handleFileSave} initialUrl={editingFileIndex !== null ? section.items?.[editingFileIndex].link : ''} />
       <RotationConfigModal isOpen={isRotationConfigOpen} onClose={() => setIsRotationConfigOpen(false)} section={section} pageId={pageId} onAddImage={openImageModal} />
       <FilterConfigModal isOpen={isFilterConfigOpen} onClose={() => setIsFilterConfigOpen(false)} section={section} pageId={pageId} />
       <SourceConfigModal isOpen={isSourceConfigOpen} onClose={() => setIsSourceConfigOpen(false)} section={section} pageId={pageId} />
    </div>
  );
};

export default DynamicPage;
